import {
    PluginSurfaceProvider,
    type PluginSurfaceEnvironment,
} from "@magic-md/plugin-sdk";
import {
    createElement,
    type FC,
    type ReactNode,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import pluginSurfaceCss from "../styles/plugin-surface.css?inline";

export interface PluginSurfaceProps {
    pluginName: string;
    display: "block" | "inline";
    styles?: readonly string[];
    children?: ReactNode;
}

interface SurfaceNodes extends PluginSurfaceEnvironment {
    mountRoot: HTMLElement;
    baseStyle: HTMLStyleElement | null;
    pluginStyle: HTMLStyleElement | null;
}

const sharedStyleSheets = new WeakMap<Document, Map<string, CSSStyleSheet>>();

function getSharedStyleSheet(
    ownerDocument: Document,
    cssText: string,
): CSSStyleSheet | null {
    const Sheet = ownerDocument.defaultView?.CSSStyleSheet;
    if (!Sheet || typeof Sheet.prototype.replaceSync !== "function") return null;

    let documentSheets = sharedStyleSheets.get(ownerDocument);
    if (!documentSheets) {
        documentSheets = new Map();
        sharedStyleSheets.set(ownerDocument, documentSheets);
    }
    const cached = documentSheets.get(cssText);
    if (cached) return cached;

    try {
        const sheet = new Sheet();
        sheet.replaceSync(cssText);
        documentSheets.set(cssText, sheet);
        return sheet;
    } catch {
        return null;
    }
}

function applySurfaceStyles(surface: SurfaceNodes, cssText: string): void {
    const ownerDocument = surface.mountRoot.ownerDocument;
    const baseSheet = getSharedStyleSheet(ownerDocument, pluginSurfaceCss);
    const pluginSheet = cssText
        ? getSharedStyleSheet(ownerDocument, cssText)
        : null;
    const canAdopt =
        "adoptedStyleSheets" in surface.shadowRoot &&
        !!baseSheet &&
        (!cssText || !!pluginSheet);

    if (canAdopt) {
        surface.shadowRoot.adoptedStyleSheets = pluginSheet
            ? [baseSheet, pluginSheet]
            : [baseSheet];
        surface.baseStyle?.remove();
        surface.pluginStyle?.remove();
        surface.baseStyle = null;
        surface.pluginStyle = null;
        return;
    }

    if ("adoptedStyleSheets" in surface.shadowRoot) {
        surface.shadowRoot.adoptedStyleSheets = [];
    }
    if (!surface.baseStyle) {
        surface.baseStyle = ownerDocument.createElement("style");
        surface.baseStyle.dataset.magicMdPluginBase = "";
        surface.baseStyle.textContent = pluginSurfaceCss;
        surface.shadowRoot.prepend(surface.baseStyle);
    }
    if (!surface.pluginStyle) {
        surface.pluginStyle = ownerDocument.createElement("style");
        surface.pluginStyle.dataset.magicMdPluginStyles = "";
        surface.baseStyle.after(surface.pluginStyle);
    }
    if (surface.pluginStyle.textContent !== cssText) {
        surface.pluginStyle.textContent = cssText;
    }
}

function createSurface(host: HTMLElement): SurfaceNodes {
    const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    shadowRoot.replaceChildren();
    const ownerDocument = host.ownerDocument;

    const mountRoot = ownerDocument.createElement("div");
    mountRoot.dataset.magicMdPluginMount = "";

    const portalRoot = ownerDocument.createElement("div");
    portalRoot.dataset.magicMdPluginPortal = "";

    shadowRoot.append(mountRoot, portalRoot);
    const surface = {
        shadowRoot,
        mountRoot,
        portalRoot,
        baseStyle: null,
        pluginStyle: null,
    };
    applySurfaceStyles(surface, "");
    return surface;
}

/**
 * Mandatory host boundary for plugin React UI. Every registered Renderer and
 * ConfigPanel is mounted into an open ShadowRoot owned by this component.
 */
export const PluginSurface: FC<PluginSurfaceProps> = ({
    pluginName,
    display,
    styles = [],
    children,
}) => {
    const hostRef = useRef<HTMLElement | null>(null);
    const [surface, setSurface] = useState<SurfaceNodes | null>(null);

    useLayoutEffect(() => {
        const host = hostRef.current;
        if (!host) return;
        setSurface(createSurface(host));
    }, []);

    const pluginCss = useMemo(() => styles.join("\n"), [styles]);
    useLayoutEffect(() => {
        if (surface) applySurfaceStyles(surface, pluginCss);
    }, [pluginCss, surface]);

    const environment = useMemo<PluginSurfaceEnvironment | null>(
        () => surface
            ? {
                shadowRoot: surface.shadowRoot,
                portalRoot: surface.portalRoot,
            }
            : null,
        [surface],
    );

    const host = createElement("magic-md-plugin-root", {
        ref: (node: HTMLElement | null) => {
            hostRef.current = node;
        },
        "data-plugin-name": pluginName,
        "data-display": display,
    });

    return (
        <>
            {host}
            {surface && environment
                ? createPortal(
                    <PluginSurfaceProvider value={environment}>
                        {children}
                    </PluginSurfaceProvider>,
                    surface.mountRoot,
                )
                : null}
        </>
    );
};
